import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { IconName, Icon } from '../../../UI';
import Badge from 'Components/UI/Badge';
import Ellipsis from 'Components/UI/Ellipsis';
import useHistoryPush from 'Hooks/useHistoryPush';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { GetAvatar } from 'Utils/generateThumbnail';
import { CSSTransition } from 'react-transition-group';
import { INotificationsMapAux } from 'Data/interfaces/Notification/INotificationsMapAux';
import { INotificationInAppObraCount } from 'Data/interfaces/Notification/INotification';
import { WrapperStyled } from './styles';
import './childrenWrapper.css';

export interface IMenuItemObras {
  icon?: IconName;
  obra?: IObraResponse;
  title: string;
  path: string;
  redirect: string;
  obrasList?: IObraResponse[];
  isChildren?: boolean;
  isCollapsed: boolean;
  showIconPremium?: boolean,
  existNotification?: boolean,
  valueConstructionsNotification?: number;
  counter: number;
  openList: boolean;
  notification?: INotificationsMapAux[];
  constructionSiteId?: number;
  notificationsCount: INotificationInAppObraCount[];
  onCounter: Dispatch<SetStateAction<number>>;
  onOpenList: Dispatch<SetStateAction<boolean>>;
}

const MenuItemObras: FC<IMenuItemObras> = (
  {
    icon,
    obra,
    title,
    path,
    redirect,
    obrasList,
    isChildren,
    isCollapsed,
    existNotification,
    valueConstructionsNotification = 0,
    counter,
    openList,
    notification,
    constructionSiteId,
    notificationsCount,
    onCounter,
    onOpenList,
  },
) => {
  const location = useLocation();
  const { historyPush } = useHistoryPush();

  const obras = obrasList?.filter((obra) => !obra.isArchived);

  const valueNotification = useMemo(() => {
    if (constructionSiteId) {
      return notificationsCount.find(notification => {
        return notification.ConstructionSiteId === constructionSiteId;
      })?.Count ?? 0;
    }

    return notificationsCount.reduce((acc, cur) => {
      return acc + (cur?.Count ?? 0);
    }, 0);
  }, [constructionSiteId, notificationsCount]);

  useEffect(() => {
    if (location?.pathname === '/' || location?.pathname === '/obras') {
      onCounter(1);
    } else {
      onCounter(0);
    }
  }, [onCounter, location])

  const handleToggleObras = () => {
    if (!obra && (counter === 0)) {
      historyPush(redirect);
      onOpenList(true);
    } else if (obra) {
      historyPush(redirect);
    } else {
      onOpenList(prevState => !prevState)
    }
  }

  const isActive = (): boolean => {
    if (redirect && !obra && (location?.pathname.includes(redirect) || location?.pathname === '/')) {
      return true;
    }

    const customArchives = redirect.replace('/detail', '/filelist');
    const customPlotagens = redirect.replace('/detail', '/plotagens');
    const customPlotagensArquives = redirect.replace('/detail', '/plotagens/filelist');
    const customPlotagensAReceber = redirect.replace('/detail', '/plotagens/filelist/receber');
    const customActivities = redirect.replace('/detail', '/activities/detail');
    const customViewer = redirect.replace('/detail', '/viewer/detail');
    const customEdit = redirect.replace('/detail', '/edit');

    if (
      (redirect && obra) &&
      (
        location?.pathname === redirect ||
        location?.pathname.includes(customArchives) ||
        location?.pathname.includes(customPlotagens) ||
        location?.pathname.includes(customPlotagensArquives) ||
        location?.pathname.includes(customPlotagensAReceber) ||
        location?.pathname.includes(customActivities) ||
        location?.pathname.includes(customViewer) ||
        location?.pathname.includes(customEdit)
      )
    ) {
      return true;
    }

    return false;
  };

  const isSelected = useMemo(() => {
    if (location && location.pathname.includes('obras')) {
      return true;
    }

    return false
  }, [location])

  const active = isActive();

  return (
    <WrapperStyled
      active={`${active}`}
      isselected={`${isSelected}`}
      isCollapsed={`${isCollapsed}`}
      isChildren={`${isChildren}`}
      isopen={`${openList}`}
    >
      <div
        className="obrasMenuItem"
        onClick={() => handleToggleObras()}
      >
        {icon && (
          <Icon
            icon={icon}

            customSize={isCollapsed ? 12 : 14}
            className="obrasIcon"
          />
        )}
        {obra && (
          <span className="avatar">
            <GetAvatar
              name={obra.Name}
              shape='circle'
              size={28}
              src={obra?.OriginalImageUrl || obra?.AvatarFileName}
              thumbSrc={obra?.ThumbnailImageUrl}
              thumbType="small"
            />
          </span>
        )}

        <p className="titleMenu">
          <Ellipsis phrase={title} maxLength={22}></Ellipsis>
        </p>

        {(obrasList && obras && !isCollapsed && (obras.length > 0)) && (
          <span className="obrasIconToggleWrap">
            <Icon
              icon="avancar"
              customSize={10}
              className="obrasIconChildren"
            />
          </span>
        )}

        {!isCollapsed && (valueNotification && valueNotification > 0) ? (
          <Badge type="square" value={valueNotification} style={{ marginRight: 8 }} />
        ) : null}
      </div>

      <CSSTransition
        in={openList && !isCollapsed}
        classNames='collapseListObras'
        timeout={200}
        unmountOnExit
      >
        <div className="obrasChildrenWrapper">
          {obras &&
            obras.map((obra, i) => (
              <MenuItemObras
                key={`item-menu-${i}-4`}
                isCollapsed={isCollapsed}
                obra={obra}
                title={obra.Name}
                path={`/obras/detail/${obra?.ConstructionSiteId}`}
                redirect={`/obras/detail/${obra?.ConstructionSiteId}`}
                isChildren
                counter={counter}
                openList={openList}
                notification={notification}
                constructionSiteId={obra.ConstructionSiteId}
                notificationsCount={notificationsCount}
                onCounter={onCounter}
                onOpenList={onOpenList}
              />
            ))}
        </div>
      </CSSTransition>
    </WrapperStyled>
  );
};

export default MenuItemObras;
